import React, { Component } from 'react';
import PropTypes from 'prop-types';
import range from 'lodash/range';
import styled from 'styled-components';

import Colors from '~/utils/colors';
import Link from '~/components/Link/Link';
import config from '~/config';

function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

const slugToText = (slug) => slug.split('-').map((phrase) => capitalizeFirstLetter(phrase)).join(' '); // eslint-disable-line
const textToSlug = (text) => text.split(' ').map((phrase) => phrase.toLowerCase()).join('-');

const BreadcrumbItemSpan = styled.span`
  color: ${Colors.lightGray};
  ${props => !props.noLink && `
    &:hover {
      text-decoration: underline;
    }
  `}
`;

// const getCityData = (city) => (
//   (['Bangkok', 'Ayutthaya', 'Chiang Mai', 'Phuket'].indexOf(city) >= 0)
//     ? { href: `/to/${encodeURIComponent(city)}`, text: city }
//     : { text: city, url: `/to/${encodeURIComponent(city)}` }
// );

const getCityData = (city) => ({ href: `/to/${encodeURIComponent(city)}`, text: city });

const getBreadcrumbsData = (type, data) => {
  if (type === 'TripDetail') {
    if ((data.product_type === 'ticket' && !data.tags.includes('disallow_thai'))) {
      if (data.tags.includes('luggage')) {
        return [
          {
            text: data.name,
            url: `/trip/${data.slug}`
          }
        ];
      }
      return [
        {
          href: '/article/overseas-attraction-tickets-travel-products-special-deals',
          text: 'ตั๋วคนไทย'
        },
        {
          text: data.name,
          url: `/trip/${data.slug}`
        }
      ];
    }
    const breadcrumbs = data.destination_location ? [getCityData(data.destination_location)] : [];
    return [
      ...breadcrumbs,
      {
        text: data.name,
        url: `/trip/${data.slug}`
      }
    ];
  }
  if (type === 'Search') {
    const firstArr = [getCityData(data.city)];
    if (data.h1) {
      return [
        ...firstArr,
        { text: data.h1 }
      ];
    }
    return firstArr;
  }
  if (type === 'City') {
    return [
      {
        text: data.city,
        url: `/to/${encodeURIComponent(data.city)}`
      }
    ];
  }
  if (type === 'Article') {
    if (data.slug === 'all-article') {
      return [
        {
          text: 'Article',
          url: '/article'
        }
      ];
    }
    if (data.type === 'landmark') {
      return [
        {
          text: 'Landmark',
          url: '',
        }, {
          text: data.title,
          url: `/landmark/${data.slug}`,
        }
      ];
    }
    if (data.type === 'highlight') {
      return [
        {
          text: 'Highlight',
          url: '',
        }, {
          text: data.title,
          url: `/highlights/${data.slug}`,
        }
      ];
    }
    if (data.type === 'promotion') {
      return [
        {
          text: 'Promotions',
          href: '/promotions',
        }, {
          text: data.title,
          url: `/promotions/${data.slug}`,
        }
      ];
    }
    if (data.tags && data.tags.find((tag) => tag.startsWith('article/city/'))) {
      const city = data.tags.find((tag) => tag.startsWith('article/city/')).match(/^article\/city\/(.+)$/)[1];
      return [
        {
          text: 'Article',
          href: '/article'
        }, {
          text: city,
          href: `/article/${textToSlug(city)}`
        }, {
          text: data.title,
          url: `/article/${data.slug}`
        }
      ];
    }
    return [
      {
        text: 'Article',
        href: '/article'
      }, {
        text: data.title,
        url: `/article/${data.slug}`
      }
    ];
  }
  if (type === 'affiliate') {
    return [
      { text: 'Affiliate', url: '/affiliate' }
    ];
  }
  if (type === 'about-us') {
    return [
      { text: 'About Us', url: '/about-us' }
    ];
  }
  if (type === 'hosted-by-locals') {
    return [
      { text: 'Hosted by locals', url: '/hosted-by-locals' }
    ];
  }
  return null;
};

const Separator = () => (
  <i
    className="fa fa-angle-right"
    style={{ padding: '0 5px', color: Colors.lightGray }}
  />
);

const BreadcrumbItem = ({ href, text, position, url }) => (
  href
    ? (
      <span
        itemProp="itemListElement"
        itemScope
        itemType="http://schema.org/ListItem"
      >
        <Link
          itemProp="item"
          to={href}
        >
          <BreadcrumbItemSpan itemProp="name">
            {text}
          </BreadcrumbItemSpan>
          <meta itemProp="position" content={position} />
          <meta itemProp="item" itemType="http://schema.org/WebPage" content={`${config.baseURL.en}${href}`} />
        </Link>
      </span>
    )
    : (
      <span
        itemProp="itemListElement"
        itemScope
        itemType="http://schema.org/ListItem"
      >
        <BreadcrumbItemSpan noLink itemProp="name">
          {text}
        </BreadcrumbItemSpan>
        <meta itemProp="position" content={position} />
        <meta itemProp="item" itemType="http://schema.org/WebPage" content={`${config.baseURL.en}${url}`} />
      </span>
    )
);

BreadcrumbItem.propTypes = {
  href: PropTypes.string,
  text: PropTypes.string,
  position: PropTypes.string,
};

const BreadcrumbSpan = styled.span`
  font-size: 12px;
  font-weight: 400;
  padding-top: 10px;
  display: block;
  @media (max-width: 700px) {
    padding-top: 10px;
    padding-bottom: 0;
  }
`;

export default class Breadcrumbs extends Component {
  static propTypes = {
    type: PropTypes.string.isRequired,
    customStyle: PropTypes.object,
    data: PropTypes.object,
  };

  shouldComponentUpdate(nextProps) {
    if (this.props.type !== nextProps.type) {
      return true;
    }
    if (this.props.data !== nextProps.data) {
      return true;
    }
    return false;
  }

  render() {
    const { type, customStyle, data } = this.props;
    const breadcrumbsData = getBreadcrumbsData(type, data);
    const withHomeData = [{ text: 'Home', href: '/' }].concat(breadcrumbsData);
    return (
      <BreadcrumbSpan
        style={customStyle}
        itemScope
        itemType="http://schema.org/BreadcrumbList"
      >
        {
          range((withHomeData.length * 2) - 1).map((i) => (
            i % 2 === 1
              ? <Separator key={i} />
              : <BreadcrumbItem {...withHomeData[i / 2]} position={((i + 2) / 2).toString()} key={i} />
          ))
        }
      </BreadcrumbSpan>
    );
  }
}
